import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { MapPin, Phone, Mail, Clock, Send, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { submitContact } from "../lib/api";

const schema = z.object({
    nome: z.string().min(2, "Inserisci il tuo nome (almeno 2 caratteri)").max(200),
    email: z.string().email("Inserisci un indirizzo email valido"),
    telefono: z.string().max(50).optional().or(z.literal("")),
    oggetto: z.string().min(3, "Indica l'oggetto della richiesta").max(200),
    messaggio: z.string().min(10, "Il messaggio deve essere di almeno 10 caratteri").max(8000),
    richiamo_telefonico: z.boolean().default(false),
    consenso_privacy: z.literal(true, { errorMap: () => ({ message: "È necessario accettare l'informativa privacy" }) }),
});

export default function ContactSection({
    compact = false,
    defaultOggetto = "",
    defaultMessaggio = "",
    sectionId = "contatti",
    eyebrow = "Contatti",
    heading = null, // custom heading override for compact mode
    testId = "contact-section",
} = {}) {
    const [status, setStatus] = useState({ state: "idle", message: "" });
    const {
        register,
        handleSubmit,
        reset,
        watch,
        formState: { errors, isSubmitting },
    } = useForm({
        resolver: zodResolver(schema),
        defaultValues: {
            richiamo_telefonico: false,
            consenso_privacy: false,
            oggetto: defaultOggetto,
            messaggio: defaultMessaggio,
        },
    });

    const richiamoChecked = watch("richiamo_telefonico");

    const onSubmit = async (data) => {
        setStatus({ state: "loading", message: "" });
        try {
            const payload = { ...data, telefono: data.telefono || null };
            await submitContact(payload);
            setStatus({
                state: "success",
                message: "Grazie. La sua richiesta è stata inviata con successo allo Studio. Verrà ricontattato al più presto.",
            });
            reset({
                richiamo_telefonico: false,
                consenso_privacy: false,
                oggetto: defaultOggetto,
                messaggio: defaultMessaggio,
            });
        } catch (err) {
            console.error(err);
            setStatus({
                state: "error",
                message: "Si è verificato un errore nell'invio. La preghiamo di riprovare o di contattarci telefonicamente.",
            });
        }
    };

    const inputClass = "w-full bg-white border border-slate-300 px-4 py-3.5 text-navy placeholder:text-slate-400 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold transition-colors font-sans";
    const labelClass = "block text-[11px] uppercase tracking-[0.25em] font-bold text-navy mb-2";
    const fieldId = (name) => `${sectionId}-${name}`;

    return (
        <section
            id={sectionId}
            data-testid={testId}
            className={compact ? "pt-2 pb-10" : "py-24 lg:py-36 bg-white"}
        >
            <div className={compact ? "w-full" : "max-w-7xl mx-auto px-6 lg:px-10"}>
                <div className={compact ? "" : "grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20"}>
                    {/* Info column (only in full mode) */}
                    {!compact && (
                    <div className="lg:col-span-5">
                        <div className="flex items-center gap-4 mb-6">
                            <span className="gold-rule" />
                            <span className="label-eyebrow">{eyebrow}</span>
                        </div>

                        <h2 className="font-serif text-navy text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight mb-8">
                            Una prima
                            <span className="italic text-gold-deep"> valutazione</span> del Suo caso.
                        </h2>

                        <p className="text-slate-700 text-base md:text-lg leading-relaxed mb-12">
                            Il primo incontro conoscitivo, finalizzato all'esposizione e alla valutazione del caso, è sempre <strong className="text-gold-deep">gratuito</strong>.
                        </p>

                        <div className="space-y-6">
                            <div className="flex items-start gap-4" data-testid="contact-info-address">
                                <div className="w-10 h-10 flex-shrink-0 border border-gold flex items-center justify-center">
                                    <MapPin size={18} className="text-gold-deep" />
                                </div>
                                <div>
                                    <div className="text-[11px] uppercase tracking-[0.25em] font-bold text-gold-deep mb-1">Sedi</div>
                                    <div className="text-navy text-base">Corso Vittorio Emanuele II, 142/D</div>
                                    <div className="text-slate-600 text-base">29121 Piacenza (PC) — Milano (MI) — Lecce (LE)</div>
                                </div>
                            </div>

                            <div className="flex items-start gap-4" data-testid="contact-info-phone">
                                <div className="w-10 h-10 flex-shrink-0 border border-gold flex items-center justify-center">
                                    <Phone size={18} className="text-gold-deep" />
                                </div>
                                <div>
                                    <div className="text-[11px] uppercase tracking-[0.25em] font-bold text-gold-deep mb-1">Telefono</div>
                                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-navy text-base">
                                        <a href="tel:0523468737" className="hover:text-gold-deep">0523 468737</a>
                                        <span className="text-slate-300">/</span>
                                        <a href="tel:3270345624" className="hover:text-gold-deep">327 0345624</a>
                                    </div>
                                </div>
                            </div>

                            <div className="flex items-start gap-4" data-testid="contact-info-email">
                                <div className="w-10 h-10 flex-shrink-0 border border-gold flex items-center justify-center">
                                    <Mail size={18} className="text-gold-deep" />
                                </div>
                                <div>
                                    <div className="text-[11px] uppercase tracking-[0.25em] font-bold text-gold-deep mb-1">Email</div>
                                    <a href="mailto:marco.candido@pabclex.it" className="text-navy text-base hover:text-gold-deep break-all">marco.candido@pabclex.it</a>
                                </div>
                            </div>

                            <div className="flex items-start gap-4" data-testid="contact-info-hours">
                                <div className="w-10 h-10 flex-shrink-0 border border-gold flex items-center justify-center">
                                    <Clock size={18} className="text-gold-deep" />
                                </div>
                                <div>
                                    <div className="text-[11px] uppercase tracking-[0.25em] font-bold text-gold-deep mb-1">Orari</div>
                                    <div className="text-navy text-base">Lun – Ven, 9:00 – 18:00</div>
                                </div>
                            </div>
                        </div>
                    </div>
                    )}

                    {/* Form column */}
                    <div className={compact ? "" : "lg:col-span-7"}>
                        <form
                            onSubmit={handleSubmit(onSubmit)}
                            data-testid="contact-form"
                            className="bg-[#FAFAFA] border border-slate-200 p-8 lg:p-12 relative"
                        >
                            <div className="absolute -top-px left-0 w-24 h-[2px] bg-gold" />

                            <h3 className="font-serif text-2xl text-navy mb-2">{heading || "Modulo di Contatto"}</h3>
                            <p className="text-slate-600 text-sm mb-8">I campi contrassegnati con * sono obbligatori.</p>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                                <div>
                                    <label className={labelClass} htmlFor={fieldId("nome")}>Nome e Cognome *</label>
                                    <input
                                        id={fieldId("nome")}
                                        type="text"
                                        data-testid="contact-input-nome"
                                        className={inputClass}
                                        placeholder="Mario Rossi"
                                        {...register("nome")}
                                    />
                                    {errors.nome && <p className="text-red-600 text-xs mt-2" data-testid="error-nome">{errors.nome.message}</p>}
                                </div>

                                <div>
                                    <label className={labelClass} htmlFor={fieldId("email")}>Email *</label>
                                    <input
                                        id={fieldId("email")}
                                        type="email"
                                        data-testid="contact-input-email"
                                        className={inputClass}
                                        placeholder="mario.rossi@esempio.it"
                                        {...register("email")}
                                    />
                                    {errors.email && <p className="text-red-600 text-xs mt-2" data-testid="error-email">{errors.email.message}</p>}
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                                <div>
                                    <label className={labelClass} htmlFor={fieldId("telefono")}>Telefono</label>
                                    <input
                                        id={fieldId("telefono")}
                                        type="tel"
                                        data-testid="contact-input-telefono"
                                        className={inputClass}
                                        placeholder="+39 ___ ______"
                                        {...register("telefono")}
                                    />
                                </div>

                                <div>
                                    <label className={labelClass} htmlFor={fieldId("oggetto")}>Oggetto *</label>
                                    <input
                                        id={fieldId("oggetto")}
                                        type="text"
                                        data-testid="contact-input-oggetto"
                                        className={inputClass}
                                        placeholder="Es. Licenziamento, mobbing..."
                                        {...register("oggetto")}
                                    />
                                    {errors.oggetto && <p className="text-red-600 text-xs mt-2" data-testid="error-oggetto">{errors.oggetto.message}</p>}
                                </div>
                            </div>

                            <div className="mb-6">
                                <label className={labelClass} htmlFor={fieldId("messaggio")}>Messaggio *</label>
                                <textarea
                                    id={fieldId("messaggio")}
                                    rows={6}
                                    data-testid="contact-input-messaggio"
                                    className={`${inputClass} resize-y min-h-[140px]`}
                                    placeholder="Descriva sinteticamente la Sua situazione..."
                                    {...register("messaggio")}
                                />
                                {errors.messaggio && <p className="text-red-600 text-xs mt-2" data-testid="error-messaggio">{errors.messaggio.message}</p>}
                            </div>

                            {/* Richiamo telefonico — REQUESTED EXPLICITLY BY USER */}
                            <div className={`mb-6 p-5 border transition-colors ${richiamoChecked ? "border-gold bg-gold/5" : "border-slate-300 bg-white"}`}>
                                <label className="flex items-start gap-3 cursor-pointer" data-testid="contact-richiamo-label">
                                    <input
                                        type="checkbox"
                                        data-testid="contact-input-richiamo"
                                        className="mt-1 w-5 h-5 accent-[#D4AF37] cursor-pointer flex-shrink-0"
                                        {...register("richiamo_telefonico")}
                                    />
                                    <div>
                                        <div className="font-semibold text-navy text-base">
                                            Desidero essere ricontattato telefonicamente dall'Avvocato
                                        </div>
                                        <div className="text-slate-600 text-sm mt-1">
                                            Selezionando questa opzione, lo Studio si impegna a richiamarLa al numero di telefono indicato per un primo confronto sul Suo caso.
                                        </div>
                                    </div>
                                </label>
                            </div>

                            <div className="mb-6">
                                <label className="flex items-start gap-3 cursor-pointer" data-testid="contact-privacy-label">
                                    <input
                                        type="checkbox"
                                        data-testid="contact-input-privacy"
                                        className="mt-1 w-4 h-4 accent-[#D4AF37] cursor-pointer flex-shrink-0"
                                        {...register("consenso_privacy")}
                                    />
                                    <span className="text-xs text-slate-600 leading-relaxed">
                                        Ho letto e accetto l'<a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-gold-deep underline hover:no-underline">informativa sulla privacy</a> ai sensi del Reg. UE 2016/679 (GDPR). I miei dati saranno trattati esclusivamente per rispondere alla richiesta. *
                                    </span>
                                </label>
                                {errors.consenso_privacy && <p className="text-red-600 text-xs mt-2" data-testid="error-privacy">{errors.consenso_privacy.message}</p>}
                            </div>

                            {status.state === "success" && (
                                <div className="mb-6 p-4 border border-green-600 bg-green-50 flex items-start gap-3" data-testid="contact-success">
                                    <CheckCircle2 className="text-green-700 flex-shrink-0 mt-0.5" size={20} />
                                    <p className="text-green-800 text-sm">{status.message}</p>
                                </div>
                            )}

                            {status.state === "error" && (
                                <div className="mb-6 p-4 border border-red-500 bg-red-50 flex items-start gap-3" data-testid="contact-error">
                                    <AlertCircle className="text-red-700 flex-shrink-0 mt-0.5" size={20} />
                                    <p className="text-red-800 text-sm">{status.message}</p>
                                </div>
                            )}

                            <button
                                type="submit"
                                disabled={isSubmitting}
                                data-testid="contact-submit"
                                className="btn-gold w-full md:w-auto"
                            >
                                {isSubmitting ? (
                                    <>
                                        <Loader2 size={16} className="animate-spin" />
                                        Invio in corso...
                                    </>
                                ) : (
                                    <>
                                        <Send size={16} />
                                        Invia la richiesta
                                    </>
                                )}
                            </button>
                        </form>
                    </div>
                </div>
            </div>
        </section>
    );
}
